import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { BrandGradient } from '../components/common/PFILogo';
import { 
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ClockIcon,
  UserIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function CalendarPage() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load projects
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const projectsData = projectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsData);

      // Load all tasks from all projects
      const allTasks = [];
      for (const project of projectsData) {
        const tasksSnapshot = await getDocs(collection(db, 'projects', project.id, 'tasks'));
        const projectTasks = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          projectId: project.id,
          projectName: project.name,
          customer: project.customer,
          ...doc.data()
        }));
        allTasks.push(...projectTasks);
      }
      setTasks(allTasks);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonth = new Date(year, month - 1, 0);
      const day = prevMonth.getDate() - startingDayOfWeek + i + 1;
      days.push({
        day,
        date: new Date(year, month - 1, day),
        isCurrentMonth: false,
        isPrevMonth: true
      });
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        date: new Date(year, month, day),
        isCurrentMonth: true,
        isPrevMonth: false
      });
    }

    // Add empty cells for days after the last day of the month
    const remainingCells = 42 - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      days.push({
        day,
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
        isPrevMonth: false
      });
    }

    return days;
  };

  const getEventsForDate = (date) => {
    const events = [];
    const dateString = date.toDateString();

    // Project start dates
    projects.forEach(project => {
      if (project.startDate) {
        const startDate = project.startDate.seconds ? 
          new Date(project.startDate.seconds * 1000) : new Date(project.startDate);
        if (startDate.toDateString() === dateString) {
          events.push({
            id: `project-start-${project.id}`,
            type: 'project-start',
            title: `${project.name} - Start`,
            project: project,
            time: '09:00',
            color: 'bg-green-500'
          });
        }
      }

      if (project.expectedEndDate) {
        const endDate = project.expectedEndDate.seconds ? 
          new Date(project.expectedEndDate.seconds * 1000) : new Date(project.expectedEndDate);
        if (endDate.toDateString() === dateString) {
          events.push({
            id: `project-end-${project.id}`,
            type: 'project-end',
            title: `${project.name} - Due`,
            project: project,
            time: '17:00',
            color: 'bg-red-500'
          });
        }
      }
    });

    // Task deadlines
    tasks.forEach(task => {
      if (task.dueDate) {
        const dueDate = task.dueDate.seconds ? 
          new Date(task.dueDate.seconds * 1000) : new Date(task.dueDate);
        if (dueDate.toDateString() === dateString) {
          events.push({
            id: `task-due-${task.id}`,
            type: 'task-due',
            title: task.name,
            task: task,
            time: '12:00',
            color: 'bg-orange-500'
          });
        }
      }
    });

    return events.sort((a, b) => a.time.localeCompare(b.time));
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);

    const upcomingEvents = [];
    
    for (let d = new Date(today); d <= nextWeek; d.setDate(d.getDate() + 1)) {
      const events = getEventsForDate(new Date(d));
      upcomingEvents.push(...events.map(event => ({
        ...event,
        date: new Date(d)
      })));
    }

    return upcomingEvents.sort((a, b) => a.date - b.date);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading calendar...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const days = getDaysInMonth(currentDate);
  const upcomingEvents = getUpcomingEvents();

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <h2 className="text-2xl font-bold">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
            <Link
              to="/projects/new"
              className="inline-flex items-center px-6 py-3 bg-white text-pink-600 font-semibold rounded-lg hover:bg-pink-50 transition-colors shadow-lg"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              New Project
            </Link>
          </div>
          
          <div className="flex items-start">
            <CalendarDaysIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Project Calendar</h1>
              <p className="text-pink-100 text-xl">
                Track project timelines and important dates
              </p>
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <CalendarDaysIcon className="w-5 h-5 mr-2" />
                  {upcomingEvents.length} Upcoming Events
                </div>
                <div className="flex items-center">
                  <UserIcon className="w-5 h-5 mr-2" />
                  {projects.filter(p => p.status !== 'completed').length} Active Projects
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Calendar */}
          <div className="xl:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl border border-pink-100 overflow-hidden">
              {/* Day Headers */}
              <div className="grid grid-cols-7 bg-gradient-to-r from-pink-50 to-orange-50">
                {dayNames.map(day => (
                  <div key={day} className="p-4 text-center font-semibold text-gray-700 border-r border-pink-100 last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {days.map((day, index) => {
                  const events = getEventsForDate(day.date);
                  const isCurrentDay = isToday(day.date);
                  const isSelectedDay = isSelected(day.date);

                  return (
                    <div
                      key={index}
                      className={`min-h-32 p-2 border-r border-b border-gray-100 last:border-r-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                      } ${isCurrentDay ? 'bg-blue-50' : ''} ${isSelectedDay ? 'bg-pink-50' : ''}`}
                      onClick={() => setSelectedDate(day.date)}
                    >
                      <div className={`text-sm font-semibold mb-1 ${
                        isCurrentDay ? 'text-blue-600' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                      }`}>
                        {day.day}
                        {isCurrentDay && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full inline-block ml-1"></div>
                        )}
                      </div>

                      <div className="space-y-1">
                        {events.slice(0, 3).map(event => (
                          <div
                            key={event.id}
                            className={`text-xs p-1 rounded text-white truncate ${event.color}`}
                            title={event.title}
                          >
                            {event.title}
                          </div>
                        ))}
                        {events.length > 3 && (
                          <div className="text-xs text-gray-500">
                            +{events.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Today's Date */}
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Today</h3>
              <div className="text-2xl font-bold text-blue-600">
                {new Date().getDate()}
              </div>
              <div className="text-sm text-gray-600">
                {formatDate(new Date())}
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6">
              <div className="flex items-center mb-4">
                <ClockIcon className="w-6 h-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-bold text-gray-900">Upcoming Events</h3>
              </div>
              
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.slice(0, 5).map(event => (
                    <div key={event.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-3 h-3 rounded-full ${event.color} mt-1`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">
                          {event.title}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(event.date)} at {event.time}
                        </div>
                        {event.project && (
                          <div className="text-xs text-blue-600 mt-1">
                            <Link to={`/projects/${event.project.id}`} className="hover:underline">
                              View Project
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {upcomingEvents.length > 5 && (
                    <div className="text-xs text-gray-500 text-center pt-2">
                      +{upcomingEvents.length - 5} more events this week
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <ClockIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No upcoming events</p>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
              <div className="flex items-center mb-4">
                <CheckCircleIcon className="w-6 h-6 text-green-600 mr-3" />
                <h3 className="text-lg font-bold text-gray-900">Project Status</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Projects</span>
                  <span className="font-semibold text-blue-600">
                    {projects.filter(p => p.status === 'in_progress').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Planning Phase</span>
                  <span className="font-semibold text-orange-600">
                    {projects.filter(p => p.status === 'planning').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Completed</span>
                  <span className="font-semibold text-green-600">
                    {projects.filter(p => p.status === 'completed').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Tasks</span>
                  <span className="font-semibold text-gray-900">
                    {tasks.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Event Types</h3>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-700">Project Start</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-700">Project Due</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-sm text-gray-700">Task Deadline</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-gray-700">Today</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Selected Date Details */}
        {selectedDate && (
          <div className="mt-8 bg-white rounded-2xl shadow-xl border border-pink-100 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Events for {formatDate(selectedDate)}
            </h3>
            
            {getEventsForDate(selectedDate).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getEventsForDate(selectedDate).map(event => (
                  <div key={event.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className={`w-4 h-4 rounded-full ${event.color} mt-1`}></div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{event.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">Time: {event.time}</p>
                        {event.project && (
                          <div className="mt-2">
                            <Link
                              to={`/projects/${event.project.id}`}
                              className="text-sm text-pink-600 hover:text-pink-700 font-medium"
                            >
                              View Project →
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarDaysIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No events scheduled for this date</p>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
